/**
 * Turns every mapping workspace navigation request — a tab click, a native
 * ribbon command, a Back button, a programmatic request from Validation — into
 * one guarded commit. Nothing in the workspace changes the tab or leaves the
 * screen on its own.
 */

import { useCallback, useEffect, type ReactNode } from 'react';
import {
  resolveNavigationOutcomeMessage,
  type NavigationOutcome,
  type NavigationRequest,
  type NavigationSource,
} from '../../../shared/lib/setupRevision';
import type { ValidationWorkspaceTab } from '../../../shared/stores/usePcfValidationStore';
import { useSnackbarStore } from '../../../shared/stores/useSnackbarStore';
import { getPreviousReachableScreen } from '../../../shared/stores/uiNavigationState';
import { useCurrentScreen, useNavigationHistory } from '../../../shared/stores/useUIStore';
import {
  setupBackDestination,
  workspaceTabDestination,
} from '../lib/mappingNavigationDestinations';
import {
  resolveRequestedWorkspaceTab,
  type MappingWorkspaceTabIndex,
} from '../lib/mappingWorkspacePageModel';
import { useSetupRevisionReopen } from './useSetupRevisionReopen';
import { useUnsavedTabChangeGuard } from './useUnsavedTabChangeGuard';

interface UseMappingWorkspaceNavigationArgs {
  activeTab: MappingWorkspaceTabIndex;
  setActiveTab: (tab: MappingWorkspaceTabIndex) => void;
  /** A tab another feature asked for, e.g. Validation sending the user to its report. */
  requestedWorkspaceTab: ValidationWorkspaceTab | null;
  clearRequestedWorkspaceTab: () => void;
  /** Revit project the workspace is configured against; `null` while none is open. */
  activeProjectKey: string | null;
  /** Whether the mapping configuration store holds unsaved edits. */
  isDirty: boolean;
  /** Whether a saved profile exists that Discard could restore the edits to. */
  hasVerifiedBaseline: boolean;
  onSave: () => Promise<boolean>;
  onDiscard: () => Promise<boolean>;
  /** Closes the workspace summary, guide and tour without navigating. */
  closeWorkspaceOverlays: () => void;
  /** Leaves the workspace for the previous setup screen. */
  onGoBack: () => void;
}

export interface MappingWorkspaceNavigationApi {
  requestWorkspaceTabChange: (tab: MappingWorkspaceTabIndex, source: NavigationSource) => void;
  /** Raw guard entry point for destinations that carry their own commit action. */
  requestNavigation: (
    request: NavigationRequest,
    commit: () => void
  ) => Promise<NavigationOutcome>;
  handleMappingBack: () => void;
  handleReturnToGuidedSetup: () => void;
  unsavedTabChangeDialog: ReactNode;
  isNavigationPending: boolean;
}

export function useMappingWorkspaceNavigation({
  activeTab,
  setActiveTab,
  requestedWorkspaceTab,
  clearRequestedWorkspaceTab,
  activeProjectKey,
  isDirty,
  hasVerifiedBaseline,
  onSave,
  onDiscard,
  closeWorkspaceOverlays,
  onGoBack,
}: UseMappingWorkspaceNavigationArgs): MappingWorkspaceNavigationApi {
  const { requestNavigation, unsavedTabChangeDialog, isNavigationPending } =
    useUnsavedTabChangeGuard({ isDirty, hasVerifiedBaseline, onSave, onDiscard });
  const screen = useCurrentScreen();
  const navigationHistory = useNavigationHistory();
  const { canReopen, reopen } = useSetupRevisionReopen(activeProjectKey);
  const showNavigationWarning = useSnackbarStore((state) => state.showWarning);

  const reportNavigationOutcome = useCallback(
    (outcome: NavigationOutcome) => {
      const message = resolveNavigationOutcomeMessage(outcome);
      if (message) {
        showNavigationWarning(message);
      }
    },
    [showNavigationWarning]
  );

  const requestWorkspaceTabChange = useCallback(
    (tab: MappingWorkspaceTabIndex, source: NavigationSource) => {
      // Re-selecting the tab the user already stands on is not a destination.
      if (tab === activeTab) {
        setActiveTab(tab);
        return;
      }

      void requestNavigation(workspaceTabDestination(tab, source), () => setActiveTab(tab))
        .then(reportNavigationOutcome);
    },
    [activeTab, reportNavigationOutcome, requestNavigation, setActiveTab]
  );

  const leaveWorkspace = useCallback(
    (source: NavigationSource) => {
      const target = getPreviousReachableScreen(navigationHistory, screen);

      void requestNavigation(setupBackDestination(source, target), () => {
        closeWorkspaceOverlays();
        // A revision that cannot be opened must still never hold the user here.
        if (!canReopen) {
          onGoBack();
          return;
        }

        reopen(target);
      });
    },
    [
      canReopen,
      closeWorkspaceOverlays,
      navigationHistory,
      onGoBack,
      reopen,
      requestNavigation,
      screen,
    ]
  );

  const handleReturnToGuidedSetup = useCallback(
    () => leaveWorkspace('summaryReturnToSetup'),
    [leaveWorkspace]
  );

  /** Back leaves the workspace from the Mapping tab and returns to it from any other. */
  const handleMappingBack = useCallback(() => {
    if (activeTab === 0) {
      leaveWorkspace('mappingBack');
      return;
    }

    requestWorkspaceTabChange(0, 'mappingBack');
  }, [activeTab, leaveWorkspace, requestWorkspaceTabChange]);

  useEffect(() => {
    const nextTab = resolveRequestedWorkspaceTab(requestedWorkspaceTab);
    if (nextTab === null) {
      return;
    }

    // Consume the request before asking the guard: a request the user declines
    // is spent, not replayed on the next render.
    clearRequestedWorkspaceTab();
    requestWorkspaceTabChange(nextTab, 'programmaticTab');
  }, [clearRequestedWorkspaceTab, requestWorkspaceTabChange, requestedWorkspaceTab]);

  return {
    requestWorkspaceTabChange,
    requestNavigation,
    handleMappingBack,
    handleReturnToGuidedSetup,
    unsavedTabChangeDialog,
    isNavigationPending,
  };
}
